import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, Clock, CheckCircle, RefreshCw, ChevronDown, MapPin } from 'lucide-react';
import ViolationCard from './ViolationCard';
import ViolationResponseModal from './ViolationResponseModal';

const TimeAuditTab = ({ user, onRefresh, isRefreshing, onViolationUpdate }) => {
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedViolation, setSelectedViolation] = useState(null);
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);
  const [message, setMessage] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('pending'); // 'pending', 'valid', 'corrected', 'all'
  const [locationFilter, setLocationFilter] = useState('all');
  const [locations, setLocations] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    valid: 0,
    corrected: 0
  });

  useEffect(() => {
    fetchViolations();
    fetchLocations();
  }, [statusFilter, locationFilter]);

  const fetchViolations = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      params.append('activeOnly', 'true'); // Only show violations from active uploads
      params.append('limit', '100');

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (locationFilter !== 'all') {
        params.append('locationId', locationFilter);
      }

      const response = await axios.get(`/api/time-audit/violations?${params.toString()}`);
      setViolations(response.data.violations || []);

      // If we're showing a specific status, also fetch all for stats
      if (statusFilter !== 'all') {
        const allParams = new URLSearchParams();
        allParams.append('activeOnly', 'true');
        allParams.append('limit', '500');
        if (locationFilter !== 'all') {
          allParams.append('locationId', locationFilter);
        }
        const allResponse = await axios.get(`/api/time-audit/violations?${allParams.toString()}`);
        const allViolations = allResponse.data.violations || [];

        setStats({
          total: allViolations.length,
          pending: allViolations.filter(v => v.status === 'pending').length,
          valid: allViolations.filter(v => v.status === 'valid').length,
          corrected: allViolations.filter(v => v.status === 'corrected').length
        });
      } else {
        const allViolations = response.data.violations || [];
        setStats({
          total: allViolations.length,
          pending: allViolations.filter(v => v.status === 'pending').length,
          valid: allViolations.filter(v => v.status === 'valid').length,
          corrected: allViolations.filter(v => v.status === 'corrected').length
        });
      }
    } catch (err) {
      console.error('Error fetching violations:', err);
      setError('Failed to load violations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/time-audit/manager-locations');
      setLocations(response.data.locations || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const handleRespond = (violation) => {
    setSelectedViolation(violation);
  };

  const handleSubmitResponse = async (responseData) => {
    setIsSubmittingResponse(true);
    try {
      await axios.put(`/api/time-audit/violations/${responseData.violationId}/status`, {
        status: responseData.status
      });

      const statusLabels = { valid: 'Valid', corrected: 'Corrected', pending: 'Pending' };
      setMessage(`Violation marked as ${statusLabels[responseData.status] || responseData.status}!`);
      setSelectedViolation(null);
      fetchViolations(); // Refresh the list

      // Notify parent to refresh badge count
      if (onViolationUpdate) {
        onViolationUpdate();
      }

      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Error updating violation status:', err);
      throw new Error(err.response?.data?.error || 'Failed to update violation');
    } finally {
      setIsSubmittingResponse(false);
    }
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;
    await fetchViolations();
    if (onRefresh) onRefresh();
  };

  // Group violations by date
  const groupedViolations = violations.reduce((groups, violation) => {
    const date = violation.workDate || 'Unknown';
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(violation);
    return groups;
  }, {});

  // Sort dates in descending order
  const sortedDates = Object.keys(groupedViolations).sort((a, b) => new Date(b) - new Date(a));

  // Format date for display
  const formatDateHeader = (dateString) => {
    if (dateString === 'Unknown') return 'Unknown Date';
    const date = new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-4">
      {/* Header with Stats */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Clock className="h-6 w-6 text-primary-600" />
            <h2 className="text-lg font-medium text-gray-900">Time Audit</h2>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-full transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-5 w-5 ${(isRefreshing || loading) ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats Cards - Clickable to filter */}
        <div className="grid grid-cols-4 gap-2">
          <button
            onClick={() => setStatusFilter('pending')}
            className={`rounded-lg p-3 text-center transition-all ${
              statusFilter === 'pending'
                ? 'bg-yellow-100 ring-2 ring-yellow-400'
                : 'bg-yellow-50 hover:bg-yellow-100'
            }`}
          >
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
            <div className="text-xs text-yellow-600">Pending</div>
          </button>
          <button
            onClick={() => setStatusFilter('valid')}
            className={`rounded-lg p-3 text-center transition-all ${
              statusFilter === 'valid'
                ? 'bg-red-100 ring-2 ring-red-400'
                : 'bg-red-50 hover:bg-red-100'
            }`}
          >
            <div className="text-2xl font-bold text-red-700">{stats.valid}</div>
            <div className="text-xs text-red-600">Valid</div>
          </button>
          <button
            onClick={() => setStatusFilter('corrected')}
            className={`rounded-lg p-3 text-center transition-all ${
              statusFilter === 'corrected'
                ? 'bg-green-100 ring-2 ring-green-400'
                : 'bg-green-50 hover:bg-green-100'
            }`}
          >
            <div className="text-2xl font-bold text-green-700">{stats.corrected}</div>
            <div className="text-xs text-green-600">Corrected</div>
          </button>
          <button
            onClick={() => setStatusFilter('all')}
            className={`rounded-lg p-3 text-center transition-all ${
              statusFilter === 'all'
                ? 'bg-gray-200 ring-2 ring-gray-400'
                : 'bg-gray-50 hover:bg-gray-100'
            }`}
          >
            <div className="text-2xl font-bold text-gray-700">{stats.total}</div>
            <div className="text-xs text-gray-600">Total</div>
          </button>
        </div>
      </div>

      {/* Location Filter */}
      {locations.length > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center space-x-2">
              <MapPin className="h-5 w-5 text-gray-400" />
              <span className="font-medium text-gray-700">Location Filter</span>
              {locationFilter !== 'all' && (
                <span className="bg-primary-100 text-primary-700 text-xs px-2 py-0.5 rounded-full">
                  Active
                </span>
              )}
            </div>
            <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>

          {showFilters && (
            <div className="mt-4">
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="all">All My Locations</option>
                {locations.map(location => (
                  <option key={location.id} value={location.id}>
                    {location.locationNumber} - {location.locationName}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Message */}
      {message && (
        <div className="p-3 rounded-md bg-green-50 text-green-700 text-sm flex items-center space-x-2">
          <CheckCircle className="h-5 w-5" />
          <span>{message}</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm flex items-center space-x-2">
          <AlertTriangle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Violations List */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <RefreshCw className="h-8 w-8 text-primary-600 animate-spin mx-auto mb-3" />
          <p className="text-gray-600">Loading violations...</p>
        </div>
      ) : violations.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            {statusFilter === 'pending' ? 'All Clear!' : 'No Violations Found'}
          </h3>
          <p className="text-gray-600">
            {statusFilter === 'pending'
              ? 'No pending violations to review.'
              : statusFilter === 'valid'
              ? 'No valid violations found.'
              : statusFilter === 'corrected'
              ? 'No corrected violations yet.'
              : 'No violations found matching your filters.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {sortedDates.map(date => (
            <div key={date}>
              <h3 className="text-sm font-medium text-gray-500 mb-3 px-1">
                {formatDateHeader(date)}
              </h3>
              <div className="space-y-3">
                {groupedViolations[date].map(violation => (
                  <ViolationCard
                    key={violation.id}
                    violation={violation}
                    onRespond={handleRespond}
                    onMarkSalaried={() => {
                      fetchViolations();
                      if (onViolationUpdate) onViolationUpdate();
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Response Modal */}
      {selectedViolation && (
        <ViolationResponseModal
          violation={selectedViolation}
          onSubmit={handleSubmitResponse}
          onClose={() => setSelectedViolation(null)}
          isSubmitting={isSubmittingResponse}
        />
      )}
    </div>
  );
};

export default TimeAuditTab;
